import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import apiClient from '@/api/client';
import { useQueryClient } from '@tanstack/react-query';

const OPEN_CHANNELS_KEY = 'mentara_open_channels';
const PANEL_OPEN_KEY = 'mentara_panel_open';

interface ChatPanelContextType {
  isOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  selectedChannelId: string | null;
  setSelectedChannelId: (id: string | null) => void;
  openChannelIds: string[];
  addOpenChannel: (id: string) => void;
  removeOpenChannel: (id: string) => void;
  clearOpenChannels: () => void;
  /** Opens chat panel and creates/selects a DM with the specified user */
  openChatWithUser: (userId: string) => Promise<void>;
  /** Loading state for openChatWithUser */
  isOpeningChat: boolean;
}

const ChatPanelContext = createContext<ChatPanelContextType | null>(null);

// Helper to load persisted open channels from sessionStorage
function loadOpenChannels(): string[] {
  try {
    const stored = sessionStorage.getItem(OPEN_CHANNELS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

// Helper to save open channels to sessionStorage
function saveOpenChannels(channelIds: string[]) {
  try {
    sessionStorage.setItem(OPEN_CHANNELS_KEY, JSON.stringify(channelIds));
  } catch {
    // Ignore storage errors
  }
}

// Helper to load panel open state from sessionStorage
function loadPanelOpen(): boolean {
  try {
    return sessionStorage.getItem(PANEL_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

// Helper to save panel open state to sessionStorage
function savePanelOpen(isOpen: boolean) {
  try {
    sessionStorage.setItem(PANEL_OPEN_KEY, isOpen ? 'true' : 'false');
  } catch {
    // Ignore storage errors
  }
}

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(() => loadPanelOpen());
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [openChannelIds, setOpenChannelIds] = useState<string[]>(() => loadOpenChannels());
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const queryClient = useQueryClient();

  // Persist open channels whenever they change
  useEffect(() => {
    saveOpenChannels(openChannelIds);
  }, [openChannelIds]);

  // Persist panel open state whenever it changes
  useEffect(() => {
    savePanelOpen(isOpen);
  }, [isOpen]);

  const openChat = () => setIsOpen(true);
  const closeChat = () => {
    setIsOpen(false);
    // Don't clear open channels on close - let them persist
  };
  const toggleChat = () => setIsOpen(prev => !prev);

  const addOpenChannel = (id: string) => {
    if (!openChannelIds.includes(id)) {
      setOpenChannelIds(prev => [...prev, id]);
    }
  };

  const removeOpenChannel = (id: string) => {
    setOpenChannelIds(prev => prev.filter(channelId => channelId !== id));
  };

  const clearOpenChannels = () => {
    setOpenChannelIds([]);
  };

  /**
   * Opens the chat panel and selects the channel for a student-tutor conversation.
   * Uses the backend endpoint that returns the shared channel (includes admin).
   * If no channel exists, the backend creates one automatically.
   * 
   * Note: This is specifically for tutors messaging students. It uses a dedicated
   * endpoint that ensures the proper 3-way channel (admin + tutor + student) is used.
   */
  const openChatWithUser = useCallback(async (studentId: string) => {
    setIsOpeningChat(true);
    try {
      // Use the dedicated endpoint that looks up/creates the proper channel
      const response = await apiClient.get(`/chat/student-channel/${studentId}`);
      const channelId = response.data.channelId;
      
      // Invalidate channels query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      
      // Select the channel and open the panel
      setSelectedChannelId(channelId);
      addOpenChannel(channelId);
      setIsOpen(true);
    } catch (error) {
      console.error('Failed to open chat with student:', error);
    } finally {
      setIsOpeningChat(false);
    }
  }, [queryClient]);

  return (
    <ChatPanelContext.Provider value={{
      isOpen,
      openChat,
      closeChat,
      toggleChat,
      selectedChannelId,
      setSelectedChannelId,
      openChannelIds,
      addOpenChannel,
      removeOpenChannel,
      clearOpenChannels,
      openChatWithUser,
      isOpeningChat,
    }}>
      {children}
    </ChatPanelContext.Provider>
  );
}

export function useChatPanel() {
  const context = useContext(ChatPanelContext);
  if (!context) {
    throw new Error('useChatPanel must be used within a ChatPanelProvider');
  }
  return context;
}
